import {
  Page,
  Layout,
  Card,
  IndexTable,
  Text,
  Badge,
  TextField,
  Select,
  InlineStack,
  useIndexResourceState,
  Pagination,
  Banner,
  SkeletonBodyText,
  EmptyState,
  Spinner,
  Modal,
  BlockStack,
  Icon,
} from '@shopify/polaris';
import { NoteIcon } from '@shopify/polaris-icons';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOrders, useOrderDetail } from '../hooks';
import { STAGE_LABELS, STAGE_TONES, type WorkflowStage, type TrackedOrder, type OrderListQuery } from '../types';
import { StoreBadge, getDraftOrderNameColor } from '../lib/storeUtils';
import CommunicationCenter from '../components/CommunicationCenter';
import { useToast } from '../components/EmbeddedAppProvider';

// Helper to format relative time
// Handles both ISO date strings and Firestore Timestamp objects
function formatRelativeTime(timestamp: string | { _seconds: number; _nanoseconds: number } | null | undefined): string {
  if (!timestamp) return 'Unknown';

  let date: Date;

  // Handle Firestore Timestamp object format
  if (typeof timestamp === 'object' && '_seconds' in timestamp) {
    date = new Date(timestamp._seconds * 1000);
  } else {
    date = new Date(timestamp);
  }

  // Check for invalid date
  if (isNaN(date.getTime())) return 'Unknown';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

// All workflow stages for the filter
const ALL_STAGES: WorkflowStage[] = [
  'ORDER_RECEIVED',
  'DIGITAL_MOCKUP',
  'AWAITING_APPROVAL',
  'APPROVED',
  'DIGITIZED_MOLD_SENT',
  'DIGITIZED_MOLD_RECEIVED',
  'IN_PRODUCTION',
  'IN_QA',
  'READY_TO_SHIP',
  'SHIPPED',
  'COMPLETE',
  'ARCHIVED',
];

// Loading skeleton
function OrderListSkeleton() {
  return (
    <Card padding="0">
      <div style={{ padding: '16px' }}>
        <SkeletonBodyText lines={10} />
      </div>
    </Card>
  );
}

export default function OrderList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial values from URL
  const initialStage = searchParams.get('stage') as WorkflowStage | null;
  const initialSearch = searchParams.get('search') || '';
  const initialPage = parseInt(searchParams.get('page') || '1');

  // Local state for form inputs
  const [searchValue, setSearchValue] = useState(initialSearch);
  const [stageFilter, setStageFilter] = useState<string>(initialStage || 'all');
  const [currentPage, setCurrentPage] = useState(initialPage);

  // Sorting state
  type SortField = 'shopifyOrderName' | 'customerName' | 'currentStage' | 'updatedAt';
  const COLUMN_SORT_KEYS: (SortField | null)[] = ['shopifyOrderName', 'customerName', null, 'currentStage', 'updatedAt'];
  const [sortColumnIndex, setSortColumnIndex] = useState(4); // default: Updated
  const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('descending');

  // Quick-view notes modal
  const [quickViewOrderId, setQuickViewOrderId] = useState<string | null>(null);
  const { data: quickViewData, loading: quickViewLoading, refetch: refetchQuickView } = useOrderDetail(quickViewOrderId || undefined);

  const toast = useToast();

  const handleCopyOrderNumber = useCallback(async (orderName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(orderName);
      toast.show(`${orderName} Copied`);
    } catch {
      toast.show('Unable to copy order number', true);
    }
  }, [toast]);

  const handleOpenQuickView = useCallback((orderId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click navigation
    setQuickViewOrderId(orderId);
  }, []);

  const handleCloseQuickView = useCallback(() => {
    setQuickViewOrderId(null);
  }, []);

  // Search is committed on Enter / clear; the input value alone does not trigger fetches
  const [committedSearch, setCommittedSearch] = useState(initialSearch);

  const submitSearch = useCallback(() => {
    setCommittedSearch(searchValue);
    setCurrentPage(1);
  }, [searchValue]);

  const clearSearch = useCallback(() => {
    setSearchValue('');
    setCommittedSearch('');
    setCurrentPage(1);
  }, []);

  // Sort handler
  const handleSort = useCallback((headingIndex: number, direction: 'ascending' | 'descending') => {
    setSortColumnIndex(headingIndex);
    setSortDirection(direction);
    setCurrentPage(1);
  }, []);

  // Build query — exclude archived orders when showing "All Stages"
  const buildQuery = useCallback((): OrderListQuery => {
    const isAllStages = stageFilter === 'all';
    const sortField = COLUMN_SORT_KEYS[sortColumnIndex];
    return {
      stage: isAllStages ? undefined : (stageFilter as WorkflowStage),
      excludeStage: isAllStages ? 'ARCHIVED' as WorkflowStage : undefined,
      search: committedSearch || undefined,
      page: currentPage,
      limit: 20,
      sortBy: sortField || undefined,
      sortDirection: sortDirection === 'ascending' ? 'asc' : 'desc',
    };
  }, [stageFilter, committedSearch, currentPage, sortColumnIndex, sortDirection]);

  // Fetch orders with current filters
  const { data, loading, error, refetch, setQuery } = useOrders(buildQuery());

  // Update query when filters change
  useEffect(() => {
    setQuery(buildQuery());

    // Update URL params
    const params = new URLSearchParams();
    if (stageFilter !== 'all') params.set('stage', stageFilter);
    if (committedSearch) params.set('search', committedSearch);
    if (currentPage > 1) params.set('page', currentPage.toString());
    setSearchParams(params, { replace: true });
  }, [stageFilter, committedSearch, currentPage, sortColumnIndex, sortDirection, setQuery, setSearchParams]);

  // Selection state
  const orders = data?.orders || [];
  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(orders.map((o) => ({ id: o.id })));

  // Stage filter options
  const stageOptions = [
    { label: 'All Stages', value: 'all' },
    ...ALL_STAGES.map((stage) => ({
      label: `${STAGE_LABELS[stage]} (${data?.stageCounts?.[stage] || 0})`,
      value: stage,
    })),
  ];

  // Pagination
  const totalPages = data ? Math.ceil(data.total / data.limit) : 1;
  const hasNext = currentPage < totalPages;
  const hasPrevious = currentPage > 1;

  const handleNextPage = useCallback(() => {
    if (hasNext) setCurrentPage((p) => p + 1);
  }, [hasNext]);

  const handlePreviousPage = useCallback(() => {
    if (hasPrevious) setCurrentPage((p) => p - 1);
  }, [hasPrevious]);

  // Row markup
  const rowMarkup = orders.map((order: TrackedOrder, index: number) => {
    const lineItemCount = order.lineItems.length;
    const itemText = lineItemCount === 1 ? '1 item' : `${lineItemCount} items`;

    return (
      <IndexTable.Row
        id={order.id}
        key={order.id}
        selected={selectedResources.includes(order.id)}
        position={index}
        onClick={() => navigate(`/orders/${order.id}`)}
      >
        <IndexTable.Cell>
          <InlineStack gap="200" blockAlign="center">
            <span
              onClick={(e) => handleCopyOrderNumber(order.shopifyOrderName, e)}
              style={{
                cursor: 'pointer',
                color: order.isDraft ? getDraftOrderNameColor(order.shopDomain) : undefined,
              }}
              title="Click to copy order number"
            >
              <Text as="span" variant="bodyMd" fontWeight="bold">
                {order.shopifyOrderName}
              </Text>
            </span>
            {order.isDraft && <Badge tone="warning">Draft</Badge>}
            <StoreBadge shopDomain={order.shopDomain} />
          </InlineStack>
        </IndexTable.Cell>
        <IndexTable.Cell>{order.customerName}</IndexTable.Cell>
        <IndexTable.Cell>{itemText}</IndexTable.Cell>
        <IndexTable.Cell>
          <Badge tone={STAGE_TONES[order.currentStage]}>
            {STAGE_LABELS[order.currentStage]}
          </Badge>
        </IndexTable.Cell>
        <IndexTable.Cell>{formatRelativeTime(order.updatedAt)}</IndexTable.Cell>
        <IndexTable.Cell>
          <div onClick={(e) => handleOpenQuickView(order.id, e)} style={{ cursor: 'pointer', display: 'inline-flex' }}>
            <Icon source={NoteIcon} tone="subdued" />
          </div>
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  return (
    <Page
      title="Orders"
      backAction={{ content: 'Dashboard', onAction: () => navigate('/') }}
      primaryAction={{
        content: 'Add Order',
        onAction: () => navigate('/orders/add'),
      }}
    >
      {error && (
        <Layout.Section>
          <Banner
            title="Error loading orders"
            tone="critical"
            action={{ content: 'Retry', onAction: refetch }}
          >
            <p>{error}</p>
          </Banner>
        </Layout.Section>
      )}

      <Layout>
        {/* Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" blockAlign="center">
              <div
                style={{ flex: 1 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submitSearch();
                  }
                }}
              >
                <TextField
                  label="Search"
                  labelHidden
                  value={searchValue}
                  onChange={setSearchValue}
                  placeholder="Search by order #, customer name, or email — press Enter"
                  autoComplete="off"
                  clearButton
                  onClearButtonClick={clearSearch}
                />
              </div>
              <Select
                label="Stage"
                labelHidden
                options={stageOptions}
                value={stageFilter}
                onChange={setStageFilter}
              />
              {loading && <Spinner size="small" />}
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Orders Table */}
        <Layout.Section>
          {loading && orders.length === 0 ? (
            <OrderListSkeleton />
          ) : orders.length === 0 ? (
            <Card>
              <EmptyState
                heading="No orders found"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>
                  {committedSearch || stageFilter !== 'all'
                    ? 'Try adjusting your search or filter'
                    : 'Orders with tracked products will appear here'}
                </p>
              </EmptyState>
            </Card>
          ) : (
            <Card padding="0">
              <IndexTable
                resourceName={{ singular: 'order', plural: 'orders' }}
                itemCount={orders.length}
                selectedItemsCount={
                  allResourcesSelected ? 'All' : selectedResources.length
                }
                onSelectionChange={handleSelectionChange}
                sortable={[true, true, false, true, true, false]}
                sortColumnIndex={sortColumnIndex}
                sortDirection={sortDirection}
                onSort={handleSort}
                headings={[
                  { title: 'Order', id: 'order' },
                  { title: 'Customer', id: 'customer' },
                  { title: 'Products', id: 'products' },
                  { title: 'Stage', id: 'stage' },
                  { title: 'Updated', id: 'updated' },
                  { title: '', id: 'notes' },
                ]}
              >
                {rowMarkup}
              </IndexTable>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ padding: '16px', borderTop: '1px solid #e1e3e5' }}>
                  <InlineStack align="center" gap="400">
                    <Text as="span" variant="bodySm" tone="subdued">
                      Showing {(currentPage - 1) * (data?.limit || 20) + 1}-
                      {Math.min(currentPage * (data?.limit || 20), data?.total || 0)} of{' '}
                      {data?.total || 0}
                    </Text>
                    <Pagination
                      hasNext={hasNext}
                      hasPrevious={hasPrevious}
                      onNext={handleNextPage}
                      onPrevious={handlePreviousPage}
                    />
                  </InlineStack>
                </div>
              )}
            </Card>
          )}
        </Layout.Section>
      </Layout>

      {/* Quick View Notes Modal */}
      <Modal
        open={quickViewOrderId !== null}
        onClose={handleCloseQuickView}
        title={quickViewData?.order ? `Notes — ${quickViewData.order.shopifyOrderName} (${quickViewData.order.customerName})` : 'Loading...'}
        size="large"
      >
        <Modal.Section>
          {quickViewLoading ? (
            <BlockStack gap="400">
              <SkeletonBodyText lines={6} />
            </BlockStack>
          ) : quickViewData?.order ? (
            <CommunicationCenter
              order={quickViewData.order}
              approvals={quickViewData.approvals}
              inboundEmails={quickViewData.inboundEmails}
              emailLogs={quickViewData.emailLogs}
              gmailMessages={quickViewData.gmailMessages}
              onEmailSent={refetchQuickView}
            />
          ) : (
            <Text as="p" tone="subdued">Unable to load communications</Text>
          )}
        </Modal.Section>
      </Modal>
    </Page>
  );
}
